import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import type { Me } from '../api/types';
import { errorText } from '../lib/useRelay';

interface MeState {
  me: Me | null;
  loading: boolean;
  error: string | null;
  /** admin implies connect (CONTRACT section 2), so either grants use of the VPN section. */
  canUseVpn: boolean;
  isAdmin: boolean;
  reload: () => void;
}

const MeContext = createContext<MeState | null>(null);

/** Loads GET /me once per session; the whole permission UI is driven from it. */
export function MeProvider({ children }: { children: ReactNode }) {
  const { api } = useAuth();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .me()
      .then((m) => {
        if (cancelled) return;
        setMe(m);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorText(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const value = useMemo<MeState>(
    () => ({
      me,
      loading,
      error,
      canUseVpn: !!me && (me.permissions.connect || me.permissions.admin),
      isAdmin: !!me?.permissions.admin,
      reload,
    }),
    [me, loading, error, reload],
  );
  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): MeState {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error('useMe must be used inside <MeProvider>');
  return ctx;
}
